import useMapStore from "@/store/mapStore";
import { useState, useEffect } from "react";

interface ParkAssistResult {
  id: number;
  visit: {
    id: number;
    plate: {
      text: string;
    };
  };
}

export interface ParkAssistImage {
  visitId: number;
  bayId: number;
  imageUrl: string;
}

export enum FetchStatus {
  Loading = "loading",
  Loaded = "loaded",
  Error = "error",
  TooManyRequests = "tooManyRequests",
}

export default function useParkAssist({ plateText }: { plateText: string }) {
  const [imageData, setImageData] = useState<ParkAssistImage[]>([]);
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>(
    FetchStatus.Loading,
  );

  const appSession = useMapStore((state) => state.appSession);

  useEffect(() => {
    // prevent state updates after unmount
    let isMounted = true;

    // keep track of created object URLs so we can revoke them
    const createdUrls: string[] = [];

    const fetchImageData = async () => {
      try {
        setFetchStatus(FetchStatus.Loading);

        const resultsResponse = await fetch(
          `https://api.proximi.fi/park-assist-v2?query=${encodeURIComponent(
            plateText,
          )}&session_id=${appSession}`,
          {
            headers: {
              Authorization: `Bearer ${import.meta.env.VITE_PROXIMIIO_TOKEN}`,
            },
          },
        );

        if (resultsResponse.status === 429) {
          if (isMounted) {
            setFetchStatus(FetchStatus.TooManyRequests);
          }
          return;
        }

        if (!resultsResponse.ok) {
          throw new Error(
            `Request failed with status ${resultsResponse.status}`,
          );
        }

        const results: ParkAssistResult[] = await resultsResponse.json();

        if (!results.length) {
          if (isMounted) {
            setImageData([]);
            setFetchStatus(FetchStatus.Loaded);
          }
          return;
        }

        const imagePromises: Promise<ParkAssistImage | null>[] = results.map(
          async (result) => {
            try {
              const imageResponse = await fetch(
                `https://api.proximi.fi/park-assist-v2/visits/${result.visit.id}?session_id=${appSession}`,
                {
                  headers: {
                    Authorization: `Bearer ${
                      import.meta.env.VITE_PROXIMIIO_TOKEN
                    }`,
                  },
                },
              );

              if (imageResponse.status === 429) {
                setFetchStatus(FetchStatus.TooManyRequests);
                return null;
              }

              if (!imageResponse.ok) {
                console.error(
                  `Image fetch failed for visit ${result.visit.id}`,
                );
                return null;
              }

              const blob = await imageResponse.blob();
              const imageUrl = URL.createObjectURL(blob);

              createdUrls.push(imageUrl);

              return {
                visitId: result.visit.id,
                bayId: result.id,
                imageUrl,
              };
            } catch (error) {
              console.error(
                `Failed to fetch image for visit ${result.visit.id}:`,
                error,
              );

              return null;
            }
          },
        );

        const settledResults = await Promise.allSettled(imagePromises);

        const successfulImages = settledResults
          .filter(
            (
              result,
            ): result is PromiseFulfilledResult<ParkAssistImage | null> =>
              result.status === "fulfilled",
          )
          .map((result) => result.value)
          .filter((item): item is ParkAssistImage => item !== null);

        if (!isMounted) return;

        setImageData(successfulImages);

        // if at least some succeeded, treat as loaded
        setFetchStatus(FetchStatus.Loaded);
      } catch (error) {
        console.error("Error fetching park assist data:", error);

        if (isMounted) {
          setFetchStatus(FetchStatus.Error);
        }
      }
    };

    if (plateText?.trim() && appSession) {
      fetchImageData();
    } else {
      setImageData([]);
      setFetchStatus(FetchStatus.Loading);
    }

    return () => {
      isMounted = false;

      // cleanup blob URLs
      createdUrls.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, [plateText, appSession]);

  return [imageData, fetchStatus] as const;
}
